import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import AppreciationMode from "./AppreciationMode";
import { useAppreciationAutoOpen } from "./useAppreciationAutoOpen";
import DropdownMenu, {
  type DropdownMenuItem,
} from "../dropdown-menu/DropdownMenu";
import IconButton from "../button/IconButton";
import GenerativeAudioThumbnail from "../media-preview/components/audio-thumbnail/GenerativeAudioThumbnail";
import {
  setActiveTrack,
  clearActiveTrack,
} from "../audio-preview/core/activeTrack";
import { useSpectrumAnalysis } from "../audio-preview/core/useSpectrumAnalysis";
import {
  selectIsAudioEnabled,
  setAudioEnabled,
} from "../../app/actions/audio/audioSlice";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import { formatTimecode } from "../../utils/timecode";

/**
 * 画面下フローティングオーディオプレイヤーバー（Apple Music 風）。
 *
 * 音源サムネイルのクリック再生を、モーダルではなく画面下に固定表示される
 * バーで行うためのグローバルコンポーネント。再生キュー（tracks）と現在
 * インデックスを受け取り、前後トラック移動・再生/一時停止・シークを提供する。
 *
 * レイアウト（Apple Music のミニプレイヤーに準拠）:
 * - 左端: 前トラック / 再生・一時停止 / 次トラック
 * - 中央: サムネイル + 作品名・ユーザー名（クリックでプロフィールへ）
 * - 右端: ３点リーダー（鑑賞モードで観る / 共有リンクをコピー）+ 閉じるボタン
 * - 下段: シークバー + 時間表示（左: 現在時間、右: 総時間。メッセンジャーの
 *   compactAudio と同じデザイン）
 *
 * - トラック切替時は自動再生（クリック起点のためブラウザの autoplay 制限は通る）
 * - 再生終了時は先頭にシークして停止（バーは残す）
 * - 表示中は body に padding-bottom を確保し、ページ末尾のコンテンツが隠れない
 *   ようにする
 * - ジャケット画像が無いトラックは一覧と同じ生成サムネイルを出し、再生位置に
 *   合わせて動かす。再生状態は activeTrack 経由で一覧側にも伝わり、画面内に
 *   居るサムネイルは同じ動きを続ける
 *
 * 使用例: PROFILE（ProfileMediaSection）/ GALLERY（Gallery）の音源作品再生。
 */

export interface AudioPlayerBarTrack {
  id: string;
  title: string;
  /** 音源 URL。 */
  src: string;
  /** サムネイル画像 URL。未指定なら一覧と同じ生成サムネイルを表示。 */
  thumbnailSrc?: string;
  /** 生成サムネイル用。波形 peaks JSON の URL（動きの駆動信号になる）。 */
  waveformUrl?: string | null;
  /** クリエイター表示名。指定時は作品名の下に表示する。 */
  creatorName?: string;
  /** クリエイターのプロフィール slug。指定時は名前クリックでプロフィールへ遷移。 */
  creatorSlug?: string;
  /**
   * この作品の共有リンク（呼び出し側でディープリンクを組み立てて渡す）。
   * 指定時のみ３点メニューに「共有リンクをコピー」を表示する。
   */
  shareUrl?: string;
  /**
   * 字幕テキスト（「[M:SS] テキスト」の行形式。作品編集で登録）。
   * 鑑賞モードで再生位置に合わせてテロップ表示する。
   */
  subtitleText?: string;
}

export interface AudioPlayerBarProps {
  /** 再生キュー（呼び出し側で音源作品のみ抽出して渡す）。 */
  tracks: AudioPlayerBarTrack[];
  /** 再生中トラックの tracks 内インデックス。 */
  currentIndex: number;
  /** 前後トラック移動時に呼ばれる。 */
  onIndexChange: (_index: number) => void;
  /** 右端の閉じるボタン押下時に呼ばれる（バーの破棄は呼び出し側が行う）。 */
  onClose: () => void;
  /** 他の固定バーと共存する場合の下端オフセット（px）。既定 0。 */
  bottomOffset?: number;
  /**
   * 最初のトラックを鑑賞モードで開く。共有リンク（`?work=`）から来たときに
   * 使い、受け取った人がまず作品として観られるようにする。
   * 開くのは 1 回だけで、以降の曲送りには影響しない。
   */
  openInAppreciation?: boolean;
}

/** パネルのおおよその高さ（body padding の確保に使う）。 */
const BAR_CONTENT_HEIGHT = 96;

// ===== Styled =====

/** 画面下に浮かせるための位置決めレイヤー（中央寄せ）。見た目はパネル側が持つ。 */
const BarContainer = styled.div<{ $bottomOffset: number }>`
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: ${({ $bottomOffset }) =>
    `calc(${16 + $bottomOffset}px + env(safe-area-inset-bottom))`};
  z-index: 900; /* Modal(1000) 未満、ページ内固定バー(4)より上 */
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

/** ActionPanel（CommentField）と同じ半透明グラスの浮遊パネル。 */
const BarPanel = styled.div`
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 740px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  /* From https://css.glass（CommentField / WizardActionPanel と同値） */
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);
  padding: 10px 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/** タイトル部。ellipsis のために min-width: 0 が必要（utility に無い）。 */
const TrackInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CreatorLink = styled.button`
  /* button 要素自身のフォント由来のストラットで行箱が膨らまないよう
     flex にして内側 span（line-height 100%）の高さにフィットさせる */
  display: flex;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  width: fit-content;
  max-width: 100%;
  text-align: left;

  &:hover span {
    color: var(--soremo-blue);
  }
`;

const Thumbnail = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

/** 生成サムネイルの置き場。中身は absolute で敷かれるので枠だけ用意する。 */
const GenerativeThumbnail = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
`;

// シークバー + 時間表示（メッセンジャー AudioPreview の compactAudio と同デザイン）。

const SeekArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const SeekBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  touch-action: none;
`;

const SeekBarTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 1.5px;
  background: var(--soremo-border);
  overflow: hidden;
`;

const SeekBarFill = styled.div`
  height: 100%;
  background: var(--soremo-blue);
  border-radius: 1.5px;
`;

const SeekBarThumb = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--soremo-blue);
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const TimeRow = styled.div.attrs({
  className: "bodytext-11",
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--soremo-light-gray);
  line-height: 1;
  user-select: none;
`;

// ===== 本体 =====

const AudioPlayerBar: React.FC<AudioPlayerBarProps> = ({
  tracks,
  currentIndex,
  onIndexChange,
  onClose,
  bottomOffset = 0,
  openInAppreciation = false,
}) => {
  const dispatch = useDispatch();
  const isAudioEnabled = useSelector(selectIsAudioEnabled) as boolean;

  const audioRef = useRef<HTMLAudioElement>(null);
  const seekBarRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // 鑑賞モード（フルスクリーンオーバーレイ）。audio 要素はバーが持ち続ける。
  const [showAppreciation, setShowAppreciation] = useAppreciationAutoOpen(
    openInAppreciation,
    Boolean(tracks[currentIndex]),
  );

  const track = tracks[currentIndex] as AudioPlayerBarTrack | undefined;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < tracks.length - 1;

  // トラック切替（src 変更）時に先頭から自動再生する。
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !track?.src) return;
    setCurrentTime(0);
    setDuration(0);
    dispatch(setAudioEnabled(true));
    audio.play().catch(() => {
      // autoplay がブロックされた場合は一時停止表示のまま（ユーザー操作で再生）。
      setIsPlaying(false);
    });
  }, [track?.src, dispatch]);

  // バー表示中はページ末尾のコンテンツが隠れないよう body に余白を確保する。
  useEffect(() => {
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `${BAR_CONTENT_HEIGHT + 32}px`;
    return () => {
      document.body.style.paddingBottom = prev;
    };
  }, []);

  // アンマウント時に再生を確実に停止する（AudioPreview と同じ後始末）。
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio?.pause();
    };
  }, []);

  const getCurrentTime = useCallback(
    () => audioRef.current?.currentTime ?? 0,
    [],
  );

  // 実際に音を鳴らしているのはこの要素だけなので、周波数解析もここで行い、
  // 結果を activeTrack 経由で一覧のサムネイルへ配る。
  const { crossOrigin, readSpectrum, handleMediaError } = useSpectrumAnalysis(
    audioRef,
    track?.src,
    isPlaying,
    !isAudioEnabled,
  );

  // 解析を諦めて要素を作り直したときは再生を引き継ぐ（ユーザーは再生を指示済み）。
  const analysisFallbackRef = useRef(false);
  useEffect(() => {
    if (!analysisFallbackRef.current) return;
    analysisFallbackRef.current = false;
    audioRef.current?.play().catch(() => {});
  }, [crossOrigin]);

  // 一覧に並んだサムネイルへ再生を伝える。画面内に居るサムネイルは
  // この再生位置に合わせて動き続ける。
  useEffect(() => {
    const key = track?.src;
    if (!key) return;
    setActiveTrack({ key, isPlaying, getCurrentTime, readSpectrum });
    return () => clearActiveTrack(key);
  }, [track?.src, isPlaying, getCurrentTime, readSpectrum]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      dispatch(setAudioEnabled(true));
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [dispatch]);

  // 再生終了: 先頭にシークして停止（バーは残す。ユーザー確認済みの仕様）。
  const handleEnded = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = 0;
    setCurrentTime(0);
    setIsPlaying(false);
  }, []);

  // 鑑賞モードからの秒指定シーク。
  const seekToSeconds = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      const max = Number.isFinite(audio.duration) ? audio.duration : duration;
      audio.currentTime = Math.max(0, Math.min(max || 0, seconds));
      setCurrentTime(audio.currentTime);
    },
    [duration],
  );

  // --- シーク操作（ポインター位置 → 再生位置） ---
  const seekToPosition = useCallback(
    (clientX: number) => {
      const audio = audioRef.current;
      const bar = seekBarRef.current;
      if (!audio || !bar || !duration) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.min(
        Math.max((clientX - rect.left) / rect.width, 0),
        1,
      );
      audio.currentTime = ratio * duration;
      setCurrentTime(audio.currentTime);
    },
    [duration],
  );

  const handleSeekPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      seekToPosition(e.clientX);
    },
    [seekToPosition],
  );

  const handleSeekPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.buttons === 0) return;
      seekToPosition(e.clientX);
    },
    [seekToPosition],
  );

  const handleSeekPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.releasePointerCapture(e.pointerId);
    },
    [],
  );

  // プロフィールは別タブで開く（今のページと再生バーは残し、再生位置も保持）。
  // 開いた先と音が重ならないよう、こちらの再生は一時停止する。
  // 公開 URL は `soremo.jp/{slug}`（開発サーバもルート直下配信のため同形）。
  const goToProfile = useCallback(() => {
    if (!track?.creatorSlug) return;
    audioRef.current?.pause();
    window.open(`/${track.creatorSlug}`, "_blank", "noopener,noreferrer");
  }, [track?.creatorSlug]);

  // ３点リーダーのメニュー。プロフィール遷移はアーティスト名クリック、
  // 閉じる操作は右端の × ボタンが担う。
  const { copy: copyShareUrl } = useCopyToClipboard({
    successMessage: "共有リンクをコピーしました",
  });
  const menuItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      {
        label: "鑑賞モードで観る",
        icon: "theaters",
        onClick: () => setShowAppreciation(true),
      },
    ];
    const shareUrl = track?.shareUrl;
    if (shareUrl) {
      items.push({
        label: "共有リンクをコピー",
        icon: "link",
        onClick: () => {
          void copyShareUrl(shareUrl);
        },
      });
    }
    return items;
    // setShowAppreciation は useState の setter なので不変だが、カスタムフック
    // 越しだと lint が判定できないので明示しておく。
  }, [track?.shareUrl, copyShareUrl, setShowAppreciation]);

  if (!track) return null;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  // アーティスト名（slug があればプロフィールへのリンクとして包む）
  const creatorNameText = (
    <span className="bodytext-11 u-line-height-100 u-text-light-gray u-text-ellipsis">
      {track.creatorName}
    </span>
  );

  return (
    <BarContainer
      $bottomOffset={bottomOffset}
      role="region"
      aria-label="オーディオプレイヤー"
    >
      <BarPanel>
        {/* 上段: 左端コントロール + トラック情報 + メニュー */}
        <div className="u-row u-gap16">
          {/* 左端: 前トラック / 再生 / 次トラック */}
          <div className="u-row u-gap8" style={{ flexShrink: 0 }}>
            <IconButton
              icon="skip_previous"
              filled
              size={28}
              color="var(--soremo-gray)"
              disabled={!hasPrev}
              onClick={() => hasPrev && onIndexChange(currentIndex - 1)}
              ariaLabel="前のトラック"
            />
            <IconButton
              icon={isPlaying ? "pause" : "play_arrow"}
              filled
              size={36}
              color="var(--soremo-gray)"
              onClick={togglePlay}
              ariaLabel={isPlaying ? "一時停止" : "再生"}
            />
            <IconButton
              icon="skip_next"
              filled
              size={28}
              color="var(--soremo-gray)"
              disabled={!hasNext}
              onClick={() => hasNext && onIndexChange(currentIndex + 1)}
              ariaLabel="次のトラック"
            />
          </div>

          {/* 中央: サムネ + 作品名・ユーザー名 */}
          <div className="u-row u-gap8" style={{ flex: 1, minWidth: 0 }}>
            {track.thumbnailSrc ? (
              <Thumbnail src={track.thumbnailSrc} alt="" />
            ) : (
              <GenerativeThumbnail>
                <GenerativeAudioThumbnail
                  src={track.src}
                  waveformUrl={track.waveformUrl}
                  isPlaying={isPlaying}
                  getCurrentTime={getCurrentTime}
                  readSpectrum={readSpectrum}
                />
              </GenerativeThumbnail>
            )}
            {/* 作品名・ユーザー名はユーザーリスト（UserListRow）と同じ
                タイポグラフィ（heading-spacing-13 / 行間 100% / 縦 gap 4px） */}
            <TrackInfo className="u-col u-gap4">
              <span className="heading-spacing-13 u-line-height-100 u-text-ellipsis u-w100">
                {track.title}
              </span>
              {track.creatorName &&
                (track.creatorSlug ? (
                  <CreatorLink
                    type="button"
                    onClick={goToProfile}
                    aria-label={`${track.creatorName} のプロフィールを見る`}
                  >
                    {creatorNameText}
                  </CreatorLink>
                ) : (
                  creatorNameText
                ))}
            </TrackInfo>
          </div>

          {/* 右端: ３点リーダー（共有リンクをコピー）+ 閉じる */}
          <div className="u-row u-gap8" style={{ flexShrink: 0 }}>
            {menuItems.length > 0 && (
              <DropdownMenu items={menuItems} placement="top-end" />
            )}
            <IconButton
              icon="close"
              onClick={onClose}
              ariaLabel="プレイヤーを閉じる"
            />
          </div>
        </div>

        {/* 下段: シークバー + 時間表示（左: 現在時間、右: 総時間） */}
        <SeekArea>
          <SeekBarContainer
            ref={seekBarRef}
            onPointerDown={handleSeekPointerDown}
            onPointerMove={handleSeekPointerMove}
            onPointerUp={handleSeekPointerUp}
            aria-label="再生位置"
          >
            <SeekBarTrack>
              <SeekBarFill style={{ width: `${progressPercent}%` }} />
            </SeekBarTrack>
            <SeekBarThumb style={{ left: `${progressPercent}%` }} />
          </SeekBarContainer>
          <TimeRow>
            <span>{formatTimecode(currentTime)}</span>
            <span>{formatTimecode(duration)}</span>
          </TimeRow>
        </SeekArea>

        {/* crossOrigin は読み込み開始前に確定していないと効かないため、値が
            変わるときは属性を書き換えるのではなく要素ごと作り直す。 */}
        <audio
          key={crossOrigin ?? "plain"}
          ref={audioRef}
          crossOrigin={crossOrigin}
          src={track.src}
          muted={!isAudioEnabled}
          preload="metadata"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleEnded}
          onError={() => {
            analysisFallbackRef.current = handleMediaError();
          }}
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onDurationChange={(e) => setDuration(e.currentTarget.duration)}
          style={{ display: "none" }}
        />

        {/* 鑑賞モード（body へポータル描画。バーは背後に残り audio を持ち続ける） */}
        {showAppreciation && (
          <AppreciationMode
            track={track}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => hasPrev && onIndexChange(currentIndex - 1)}
            onNext={() => hasNext && onIndexChange(currentIndex + 1)}
            onTogglePlay={togglePlay}
            onSeek={seekToSeconds}
            getCurrentTime={getCurrentTime}
            readSpectrum={readSpectrum}
            onClose={() => setShowAppreciation(false)}
          />
        )}
      </BarPanel>
    </BarContainer>
  );
};

export default AudioPlayerBar;
